import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Select } from './components/ui/select';

import AnvlVisionIcon from './assets/anvl_vision.png'; // Make sure to add this file to your assets folder

// Import the organized data
import data from './data.json';

const RiskInsightsDashboard = () => {
  const [expandedHazards, setExpandedHazards] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('2024-06');
  const [selectedOrg, setSelectedOrg] = useState(Object.keys(data.organized_items)[0]);
  const [selectedDivision, setSelectedDivision] = useState(
    Object.keys(data.organized_items[Object.keys(data.organized_items)[0]])[0]
  );
  const [hazardData, setHazardData] = useState([]);

  // Get unique months across all orgs and divisions
  const months = [...new Set(
    Object.values(data.organized_items)
      .flatMap(org => 
        Object.values(org).flatMap(division => 
          Object.keys(division)
        )
      )
  )].sort();

  // Get organizations and their divisions
  const organizations = Object.keys(data.organized_items);
  const getDivisionsForOrg = (org) => Object.keys(data.organized_items[org] || {});

  // Update divisions when org changes
  useEffect(() => {
    const divisions = getDivisionsForOrg(selectedOrg);
    setSelectedDivision(divisions[0]);
  }, [selectedOrg]);

  // Update hazard data when selections change
  useEffect(() => {
    if (selectedMonth && selectedOrg && selectedDivision) {
      const filteredData = data.organized_items[selectedOrg]?.[selectedDivision]?.[selectedMonth] || [];
      setHazardData(filteredData);
    }
  }, [selectedMonth, selectedOrg, selectedDivision]);

  const toggleHazard = (hazard) => {
    setExpandedHazards(prev =>
      prev.includes(hazard) ? prev.filter(h => h !== hazard) : [...prev, hazard]
    );
  };

  const createWorkflow = (recommendation) => {
    // Redirect to the website
    window.open('https://5e3130ae44dc983b3a.anvl.io', '_blank');
  };

  const getPriorityColor = (index) => {
    const colors = [
      'border-red-500 bg-red-100',
      'border-red-400 bg-red-50',
      'border-orange-500 bg-orange-100',
      'border-orange-400 bg-orange-50',
      'border-yellow-500 bg-yellow-100'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto mt-8 p-4">
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex justify-center w-full">
              <img src={AnvlVisionIcon} alt="Anvl Vision" className="w-256 h-24" />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Select 
                options={organizations}
                value={selectedOrg} 
                onChange={setSelectedOrg}
                placeholder="Select organization"
                className="w-full"
              />
            </div>
            <div className="flex-1">
              <Select 
                options={getDivisionsForOrg(selectedOrg)}
                value={selectedDivision} 
                onChange={setSelectedDivision}
                placeholder="Select division"
                className="w-full"
              />
            </div>
            <div className="flex-1">
              <Select 
                options={months}
                value={selectedMonth} 
                onChange={setSelectedMonth}
                placeholder="Month"
                className="w-full"
              />
            </div>
          </div>
        </div>
        {hazardData.map((hazard, index) => (
          <Card key={index} className={`mb-4 border-l-4 ${getPriorityColor(index)}`}>
            <CardHeader className="bg-gray-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-[#252A44]">
                  <button
                    onClick={() => toggleHazard(hazard.Hazard)}
                    className="flex items-center focus:outline-none"
                  >
                    {expandedHazards.includes(hazard.Hazard) ? <ChevronDown className="mr-2" /> : <ChevronRight className="mr-2" />}
                    {hazard.Hazard}
                  </button>
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">
                    {hazard.Documents.length} Documents
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">{hazard.Summary}</p>
            </CardHeader>
            {expandedHazards.includes(hazard.Hazard) && (
              <CardContent className="bg-white">
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-[#252A44]">Evidence and Related Documents</h3>
                  <ul className="space-y-2">
                    {hazard.Documents.map((doc, i) => (
                      <li key={i} className="border-b pb-2">
                        <p className="mb-1 text-sm">{doc.text}</p>
                        <a
                          href={`https://insights.anvl.com/assessment/${doc.metadata.assessmentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline flex items-center text-sm"
                        >
                          View Document <ExternalLink className="ml-1 h-4 w-4" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-[#252A44]">Recommendations</h3>
                  <p className="text-sm">{hazard.Recommendations}</p>
                  <Button onClick={() => createWorkflow(hazard.Recommendations)} className="mt-4 bg-yellow-400 text-[#252A44] hover:bg-yellow-500">
                    <Plus className="mr-2 h-4 w-4" /> Create Workflow
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RiskInsightsDashboard;